/**
 * The route-specific trees LayoutDefault renders inline (the job detail page, the search results
 * app and the backoffice shell), kept out of the bundle every page loads.
 *
 * LayoutDefault is the layout for every route, so importing these statically put the whole job
 * page and SRP (plus the map, hit cards and search panel they pull in) into the company, salary,
 * tools... pages too. Instead:
 * - Server: route-components.server-preload.ts imports them statically, so SSR renders exactly as
 *   before (synchronously, no Suspense).
 * - Browser: that module is stubbed out of client builds (vite-plugins/server-preload-stub.mjs).
 *   pages/+onRenderClient.ts awaits preloadRouteComponentsFor(pathname) before rendering, so
 *   LayoutDefault's synchronous getters below always find what the destination route needs.
 */
import { isSearchPagePathname } from '../utils/routing/is-search-page-pathname';
import { isBackofficePathname } from '../utils/backoffice/is-backoffice-pathname';
import preloaded from './route-components.server-preload';

type JobDetailPageComponent = typeof import('../pages/job/@jobId/@seoSlug/+Page').default;
type SrpAppComponent = typeof import('../features/srp/SrpApp').SrpApp;
type BackofficeShellComponent = typeof import('../components/backoffice/BackofficeShell').BackofficeShell;

interface RouteComponents {
  JobDetailPage?: JobDetailPageComponent;
  SrpApp?: SrpAppComponent;
  BackofficeShell?: BackofficeShellComponent;
}

const components: RouteComponents = { ...(preloaded as RouteComponents) };
let jobDetailPagePromise: Promise<JobDetailPageComponent> | null = null;
let srpAppPromise: Promise<SrpAppComponent> | null = null;
let backofficeShellPromise: Promise<BackofficeShellComponent> | null = null;

const JOB_DETAIL_WITH_LOCALE = /^\/(en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/[^/]+(?:\/|$)/;
const JOB_DETAIL_NO_LOCALE = /^\/(job|tyopaikka|jobb)\/([^/]+)\/[^/]+(?:\/|$)/;

/** LayoutDefault's job-route test (with or without the locale prefix, i.e. urlPathname or urlLogical). */
export function isJobDetailPathname(pathname: string): boolean {
  return JOB_DETAIL_WITH_LOCALE.test(pathname) || JOB_DETAIL_NO_LOCALE.test(pathname);
}

export function loadJobDetailPage(): Promise<JobDetailPageComponent> {
  if (components.JobDetailPage) return Promise.resolve(components.JobDetailPage);
  jobDetailPagePromise ??= import('../pages/job/@jobId/@seoSlug/+Page').then(
    (m) => (components.JobDetailPage = m.default),
    (err) => {
      jobDetailPagePromise = null;
      throw err;
    },
  );
  return jobDetailPagePromise;
}

export function loadSrpApp(): Promise<SrpAppComponent> {
  if (components.SrpApp) return Promise.resolve(components.SrpApp);
  srpAppPromise ??= import('../features/srp/SrpApp').then(
    (m) => (components.SrpApp = m.SrpApp),
    (err) => {
      srpAppPromise = null;
      throw err;
    },
  );
  return srpAppPromise;
}

export function loadBackofficeShell(): Promise<BackofficeShellComponent> {
  if (components.BackofficeShell) return Promise.resolve(components.BackofficeShell);
  backofficeShellPromise ??= import('../components/backoffice/BackofficeShell').then(
    (m) => (components.BackofficeShell = m.BackofficeShell),
    (err) => {
      backofficeShellPromise = null;
      throw err;
    },
  );
  return backofficeShellPromise;
}

/** Loads whichever route tree LayoutDefault will render for `pathname` (a no-op on the server). */
export async function preloadRouteComponentsFor(pathname: string): Promise<void> {
  if (isJobDetailPathname(pathname)) await loadJobDetailPage();
  if (isSearchPagePathname(pathname)) await loadSrpApp();
  if (isBackofficePathname(pathname)) await loadBackofficeShell();
}

function missing(name: string): null {
  console.error(`[route-components] ${name} rendered before it was loaded; await preloadRouteComponentsFor() first.`);
  return null;
}

export function getJobDetailPage(): JobDetailPageComponent | null {
  return components.JobDetailPage ?? missing('JobDetailPage');
}

export function getSrpApp(): SrpAppComponent | null {
  return components.SrpApp ?? missing('SrpApp');
}

export function getBackofficeShell(): BackofficeShellComponent | null {
  return components.BackofficeShell ?? missing('BackofficeShell');
}
